import ViewMoreButton from '@/components/Button/ViewMoreButton';
import RichText from '@/components/RichText';
import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import type { IHowTrackingWorks, IRichText } from '@/interfaces';
import type { Document } from '@contentful/rich-text-types';
import { useMemo, useState } from 'react';
import { GridSectionTitle, InsightsContainer } from '../TrackingInsights.styles';
import { interpolate } from '../interpolate.utils';
import { splitRichTextAtWords } from '../richTextPreview.utils';
import { Body, Section } from './HowTrackingWorks.styles';

// The field is a bare rich-text document: no embedded assets or entries, so nothing to link.
const NO_LINKS: IRichText['links'] = { assets: { block: [] }, entries: { block: [] } };

const PREVIEW_WORD_LIMIT = 100;

// A page carries one explainer, so a fixed id stays unique and matches between server and client.
const BODY_ID = 'how-tracking-works-body';

const renderDocument = (document: Document) => <RichText richText={{ json: document, links: NO_LINKS }} />;

export interface HowTrackingWorksProps {
  content: IHowTrackingWorks;
  /** Fills the `{carrier}` placeholder of the default heading. */
  carrierName?: string;
}

/**
 * Long-form explainer for one carrier's tracking: a single rich-text document of Heading 3
 * sections, since the sections vary per carrier.
 */
const HowTrackingWorks: React.FC<HowTrackingWorksProps> = ({ content, carrierName }) => {
  const { t } = useGlobalState();
  const [isExpanded, setIsExpanded] = useState(false);
  const preview = useMemo(
    () => (content.content ? splitRichTextAtWords(content.content, PREVIEW_WORD_LIMIT) : undefined),
    [content.content],
  );

  if (!content.content?.content?.length) return null;

  // Without a carrier name (a page with this block but no carrier information) the heading drops
  // the name rather than printing "How  Tracking Works".
  const title =
    content.title ||
    (carrierName && interpolate(t(MasterTranslation.HowTrackingWorksTitle), 'carrier', carrierName)) ||
    t(MasterTranslation.HowTrackingWorksTitleGeneric);

  // Falls back to the site-wide "View more" / "View less" while the new keys have no copy.
  const translateOr = (key: MasterTranslation, fallback: MasterTranslation) => (t(key) === key ? t(fallback) : t(key));
  const toggleLabel = isExpanded
    ? translateOr(MasterTranslation.HowTrackingWorksShowLess, MasterTranslation.ViewLess)
    : translateOr(MasterTranslation.HowTrackingWorksShowMore, MasterTranslation.ViewMore);

  return (
    <Section>
      <InsightsContainer>
        <GridSectionTitle type='h2'>{title}</GridSectionTitle>
        <Body>
          <div id={BODY_ID}>
            {!preview || isExpanded ? (
              renderDocument(content.content)
            ) : (
              // Hidden, not unmounted, so crawlers still get the whole text.
              <>
                {renderDocument(preview.preview)}
                <div hidden>{renderDocument(preview.rest)}</div>
              </>
            )}
          </div>
          {preview && (
            <ViewMoreButton
              collapsed={!isExpanded}
              label={toggleLabel}
              aria-expanded={isExpanded}
              aria-controls={BODY_ID}
              onClick={() => setIsExpanded((expanded) => !expanded)}
            />
          )}
        </Body>
      </InsightsContainer>
    </Section>
  );
};

export default HowTrackingWorks;
